/**
 * "Carrying: Rocket" — the one line on the lobby that says what a player is taking in with them.
 *
 * **IT IS ONE LINE AND THERE IS NOTHING IN THE ARENA AT ALL, WHICH IS THE POINT OF THE CARD
 * BEHIND IT.** A carried power-up is seeded through `RoomSim.takePowerUp` — the same door an orb
 * goes through — so it lands in the inventory strip the HUD already draws, wearing the same glyph,
 * with the same countdown or the same charge count. A second readout in the arena would be a
 * second drawing of a mechanic that is already on screen, which is exactly what routing it through
 * that door bought.
 *
 * **IT NAMES WHAT WILL ACTUALLY TRAVEL, NOT WHAT THE WALLET HOLDS.** `powerUpsCarriedIntoMatch` is
 * the shared rule — one of each kind, at most POWER_UP_INVENTORY_SIZE, in the registry's order —
 * and it is the same function the ticket is minted from, so the sentence somebody reads before
 * they press Play is the list they arrive holding. A player sitting on five rockets and four kinds
 * reads three names, which is the truth.
 *
 * **THE NAMES ARE THE REGISTRY'S AND THIS FILE RESTATES NONE OF THEM.** They arrive on the row,
 * resolved server-side through `POWER_UPS`; the string `'rocket'` appears here exactly nowhere, so
 * a tenth power-up is named on this line the day it is registered.
 *
 * **A guest is not asked and draws nothing.** `powerups.list` refuses a guest with a `FORBIDDEN`,
 * so the query is not *made* on their behalf rather than made and shown as an error — the
 * arrangement the profile card, the photo library and the sayings picker are already under. A
 * signed-out reader is the same case one step earlier: there is nobody to have a wallet yet.
 */

import { powerUpsCarriedIntoMatch } from '@chompdome/shared/powerups';
import { trpc } from '@/lib/trpc';

/** One wallet row, as `powerups.list` hands it over. */
interface CarriedRow {
  kind: string;
  name: string;
}

/**
 * The names, in the order the slots will be filled — or an empty list when nothing travels.
 *
 * Exported so the sentence is assertable without a render, and written as a join across the
 * shared chooser rather than a slice of the rows: the rows are already in the registry's order
 * today, and relying on that would be relying on a second copy of the rule the ticket is minted
 * from.
 */
export function carriedPowerUpNames(rows: readonly CarriedRow[]): string[] {
  const byKind = new Map(rows.map((row) => [row.kind, row.name]));
  const names: string[] = [];
  for (const kind of powerUpsCarriedIntoMatch(byKind.keys())) {
    const name = byKind.get(kind);
    if (name !== undefined) names.push(name);
  }
  return names;
}

export function CarryingLine({ isGuest }: { isGuest: boolean }) {
  const carried = trpc.powerups.list.useQuery(undefined, { enabled: !isGuest });
  const names = carriedPowerUpNames(carried.data?.items ?? []);

  // Nothing to carry is the ordinary case, and it draws nothing rather than a "Carrying: nothing"
  // that would put a row of chrome under the button for every player who has never won a round.
  // An error is silence too: a wallet that could not be read is not a reason to say something
  // false beside the one button this page exists for.
  if (names.length === 0) return null;

  return (
    <p className="text-xs font-medium text-primary" data-testid="carrying-line">
      Carrying: {names.join(', ')}
    </p>
  );
}
